import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, Power, Delete, AlertTriangle, Lock, X, Eye, EyeOff } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import OfflineService from '../../services/OfflineService';
import AuthService from '../../services/AuthService';
import logo from '../../assets/logo-getit.png';

const C = {
    primary: '#0E8164',
    accent: '#F18F27',
    secondary: '#1e293b',
    white: '#ffffff',
    tomato: '#DF2636',
};

const styles = {
    fullscreen: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'radial-gradient(circle at 50% 50%, #15803d 0%, #0E8164 100%)',
        fontFamily: 'Quicksand, system-ui, sans-serif',
        position: 'relative',
        overflow: 'hidden',
    },
    bgTexture: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V4h4V2h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V4h4V2H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        pointerEvents: 'none',
    },
    headerPill: {
        position: 'absolute',
        top: '25px',
        right: '25px',
        zIndex: 10,
        background: 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(10px)',
        padding: '8px 18px',
        borderRadius: '30px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        border: '1px solid rgba(255,255,255,0.4)',
    },
    adminButton: {
        padding: '10px 22px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '0.9rem',
        fontWeight: '700',
        color: C.primary,
        background: 'transparent',
        border: 'none',
        borderRadius: '10px',
    },
    userBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: C.primary,
    },
    logoutBtn: {
        background: C.primary,
        border: 'none',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: C.white,
    },
    card: {
        width: '100%',
        maxWidth: '420px',
        margin: '0 20px',
        padding: 'clamp(30px, 5vw, 50px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#ffffff',
        borderRadius: '40px',
        boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
        position: 'relative',
        zIndex: 5,
        boxSizing: 'border-box',
    },
    logo: {
        width: '160px',
        height: 'auto',
        objectFit: 'contain',
        marginBottom: '16px',
        filter: 'drop-shadow(0px 8px 12px rgba(0,0,0,0.12))',
    },
    subtitle: {
        fontSize: '0.85rem',
        color: C.secondary,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        margin: '0 0 32px 0',
        opacity: 0.5,
        textAlign: 'center',
    },
    pinDotContainer: {
        display: 'flex',
        gap: '18px',
        marginBottom: '32px',
    },
    pinDot: {
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        border: `2px solid #e2e8f0`,
    },
    errorPill: {
        color: C.tomato,
        marginBottom: '20px',
        fontWeight: '700',
        background: '#fee2e2',
        padding: '12px 20px',
        borderRadius: '12px',
        fontSize: '0.9rem',
        textAlign: 'center',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        boxSizing: 'border-box',
    },
    numpad: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'clamp(10px, 3vw, 18px)',
        width: '100%',
    },
    padBtn: {
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '50%',
        width: '100%',
        aspectRatio: '1 / 1',
        maxHeight: '90px',
        fontSize: 'clamp(1.4rem, 4vw, 1.8rem)',
        fontWeight: '700',
        color: C.secondary,
        cursor: 'pointer',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        outline: 'none',
        overflow: 'hidden',
    },
    padBtnClear: {
        color: C.tomato,
        borderColor: '#fca5a5',
        backgroundColor: '#fff5f5',
    },
    padBtnDelete: {
        color: '#94a3b8',
        backgroundColor: '#ffffff',
        borderColor: '#e2e8f0',
    },
    submitBtn: {
        marginTop: 'clamp(20px, 4vw, 35px)',
        width: '100%',
        padding: 'clamp(14px, 3vw, 18px)',
        background: '#E2E8F0',
        color: '#94A3B8',
        fontWeight: '700',
        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        border: 'none',
        borderRadius: '18px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        cursor: 'not-allowed',
        boxSizing: 'border-box',
    },
    submitBtnActive: {
        background: C.accent,
        color: C.white,
        cursor: 'pointer',
        boxShadow: '0 12px 25px rgba(241,143,39,0.3)',
    },
    overlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(14,129,100,0.5)',
        backdropFilter: 'blur(10px)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loginCard: {
        background: '#fff',
        borderRadius: '28px',
        padding: '40px',
        width: '100%',
        maxWidth: '380px',
        margin: '20px',
        boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        boxSizing: 'border-box',
    },
    loginHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    loginTitle: {
        fontSize: '1.3rem',
        fontWeight: '800',
        color: C.secondary,
        margin: 0,
    },
    closeBtn: {
        background: '#f1f5f9',
        border: 'none',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#64748b',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    inputLabel: {
        fontSize: '0.85rem',
        fontWeight: '700',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    inputField: {
        padding: '14px 16px',
        fontSize: '1rem',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontFamily: 'Quicksand, system-ui, sans-serif',
        outline: 'none',
        transition: 'border-color 0.2s',
        width: '100%',
        boxSizing: 'border-box',
    },
    loginBtn: {
        padding: '16px',
        background: C.primary,
        color: C.white,
        border: 'none',
        borderRadius: '14px',
        fontSize: '1rem',
        fontWeight: '700',
        cursor: 'pointer',
        fontFamily: 'Quicksand, system-ui, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
};

const PadButton = ({ children, onClick, style }) => (
    <motion.button
        whileHover={{ scale: 1.05, borderColor: C.primary }}
        whileTap={{ scale: 0.92, backgroundColor: C.accent, color: C.white, borderColor: C.accent }}
        onClick={onClick}
        style={{ ...styles.padBtn, ...style }}
    >
        {children}
    </motion.button>
);

const TokenLogin = ({ onLogin }) => {
    const { updateSessionData, clearSessionData, authenticatedUser, updateAuthenticatedUser, settings } = useAppContext();
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStep, setLoadingStep] = useState('');
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        OfflineService.clearSessionInfo();
        clearSessionData();
    }, [clearSessionData]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showLoginModal) return;
            if (e.key >= '0' && e.key <= '9') {
                if (pin.length < 4) { setPin(prev => prev + e.key); setError(''); }
            } else if (e.key === 'Backspace') {
                setPin(prev => prev.slice(0, -1));
            } else if (e.key === 'Escape') {
                setPin('');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [pin, showLoginModal]);

    const handlePad = (num) => {
        if (pin.length < 4) { setPin(prev => prev + num); setError(''); }
    };

    const handleUserLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        if (!username || !password) {
            setLoginError('Ingresa usuario y contraseña');
            return;
        }
        setLoginLoading(true);
        try {
            const userData = await AuthService.authenticateUser(username, password, settings?.apiUrl);
            updateAuthenticatedUser(userData);
            setShowLoginModal(false);
            setUsername('');
            setPassword('');
        } catch (err) {
            setLoginError(err.message || 'Credenciales incorrectas');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleActivate = async () => {
        if (pin.length !== 4 || !authenticatedUser) return;

        setLoading(true);
        setLoadingProgress(5);
        setLoadingStep('Verificando PIN...');

        let fakeInterval = null;

        try {
            setLoadingProgress(25);
            setLoadingStep('Cargando catálogo de productos...');

            let fakeProgress = 25;
            fakeInterval = setInterval(() => {
                fakeProgress = Math.min(fakeProgress + Math.random() * 6 + 1, 85);
                setLoadingProgress(Math.round(fakeProgress));
            }, 350);

            const responseData = await OfflineService.validateSession(pin);

            clearInterval(fakeInterval);
            fakeInterval = null;

            setLoadingProgress(93);
            setLoadingStep('Preparando totem...');
            await new Promise(r => setTimeout(r, 400));
            setLoadingProgress(100);
            setLoadingStep('¡Listo!');
            await new Promise(r => setTimeout(r, 350));

            const sessionDataWithPin = { ...responseData, pin };
            updateSessionData(sessionDataWithPin);
            OfflineService.saveSessionInfo(sessionDataWithPin, responseData.config_id);
            onLogin();
            navigate('/');
        } catch (err) {
            setError(err.message?.includes('PIN') ? 'PIN incorrecto' : 'Error de conexión con el servidor');
            setPin('');
            setLoading(false);
            setLoadingProgress(0);
            setLoadingStep('');
        } finally {
            if (fakeInterval) clearInterval(fakeInterval);
        }
    };

    const isReady = authenticatedUser && pin.length === 4;

    return (
        <div style={styles.fullscreen}>
            <div style={styles.bgTexture} />

            {/* Pill superior derecha */}
            <div style={styles.headerPill}>
                {!authenticatedUser ? (
                    <button onClick={() => setShowLoginModal(true)} style={styles.adminButton}>
                        <User size={18} /> Iniciar Sesión
                    </button>
                ) : (
                    <div style={styles.userBadge}>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.72rem', color: 'rgba(14,129,100,0.7)', fontWeight: '600' }}>Operador</div>
                            <div style={{ fontWeight: '700', color: C.primary, fontSize: '0.92rem' }}>{authenticatedUser.name}</div>
                        </div>
                        <button onClick={() => updateAuthenticatedUser(null)} style={styles.logoutBtn}>
                            <LogOut size={16} />
                        </button>
                    </div>
                )}
            </div>

            {/* Tarjeta central */}
            <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                style={styles.card}
            >
                <img src={logo} alt="GetIT" style={styles.logo} />
                <p style={styles.subtitle}>Totem Autoservicio</p>

                <AnimatePresence mode="wait">
                    {loading ? (
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}
                        >
                            <div style={{ position: 'relative', width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                                    style={{ position: 'absolute', inset: 0, border: `3px solid ${C.primary}`, borderTopColor: 'transparent', borderRadius: '50%' }}
                                />
                                <Power size={28} color={C.primary} />
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '1rem', fontWeight: '700', color: C.secondary, marginBottom: '4px' }}>
                                    Activando totem
                                </div>
                                <motion.div
                                    key={loadingStep}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}
                                >
                                    {loadingStep}
                                </motion.div>
                            </div>

                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ width: '100%', height: '10px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                    <motion.div
                                        animate={{ width: `${loadingProgress}%` }}
                                        transition={{ duration: 0.4, ease: 'easeOut' }}
                                        style={{
                                            height: '100%',
                                            borderRadius: '99px',
                                            background: loadingProgress === 100
                                                ? C.primary
                                                : `linear-gradient(90deg, ${C.primary} 0%, ${C.accent} 100%)`,
                                        }}
                                    />
                                </div>
                                <div style={{ textAlign: 'right', fontSize: '0.78rem', fontWeight: '700', color: '#94a3b8' }}>
                                    {loadingProgress}%
                                </div>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="pinpad"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            {/* Indicadores de PIN */}
                            <div style={styles.pinDotContainer}>
                                {[0, 1, 2, 3].map((i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            scale: i < pin.length ? 1.2 : 1,
                                            borderColor: i < pin.length ? C.primary : '#e2e8f0',
                                            backgroundColor: i < pin.length ? '#bbf7d0' : '#ffffff',
                                        }}
                                        style={styles.pinDot}
                                    />
                                ))}
                            </div>

                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        style={styles.errorPill}
                                    >
                                        <AlertTriangle size={18} /> {error}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Teclado numérico */}
                            <div style={styles.numpad}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                    <PadButton key={n} onClick={() => handlePad(n.toString())}>{n}</PadButton>
                                ))}
                                <PadButton onClick={() => setPin('')} style={styles.padBtnClear}>C</PadButton>
                                <PadButton onClick={() => handlePad('0')}>0</PadButton>
                                <PadButton onClick={() => setPin(p => p.slice(0, -1))} style={styles.padBtnDelete}>
                                    <Delete size={26} />
                                </PadButton>
                            </div>

                            <motion.button
                                animate={isReady ? { scale: [1, 1.02, 1] } : {}}
                                transition={isReady ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' } : {}}
                                whileHover={isReady ? { filter: 'brightness(1.08)' } : {}}
                                whileTap={isReady ? { scale: 0.97 } : {}}
                                onClick={handleActivate}
                                disabled={!isReady}
                                style={{ ...styles.submitBtn, ...(isReady ? styles.submitBtnActive : {}) }}
                            >
                                {authenticatedUser
                                    ? <><Power size={22} /> ACTIVAR TOTEM</>
                                    : <><Lock size={22} /> INGRESA TU USUARIO</>
                                }
                            </motion.button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Modal de login Odoo */}
            <AnimatePresence>
                {showLoginModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={styles.overlay}
                        onClick={(e) => { if (e.target === e.currentTarget) setShowLoginModal(false); }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            style={styles.loginCard}
                        >
                            <div style={styles.loginHeader}>
                                <h2 style={styles.loginTitle}>Acceso de operador</h2>
                                <button onClick={() => setShowLoginModal(false)} style={styles.closeBtn}>
                                    <X size={18} />
                                </button>
                            </div>

                            <form onSubmit={handleUserLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Usuario</label>
                                    <input
                                        style={styles.inputField}
                                        type="text"
                                        value={username}
                                        onChange={e => setUsername(e.target.value)}
                                        placeholder="usuario@empresa.com"
                                        disabled={loginLoading}
                                        autoFocus
                                    />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.inputLabel}>Contraseña</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            style={{ ...styles.inputField, paddingRight: '48px' }}
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            disabled={loginLoading}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(v => !v)}
                                            style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                {loginError && (
                                    <div style={{ color: C.tomato, fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <AlertTriangle size={16} /> {loginError}
                                    </div>
                                )}

                                <motion.button
                                    type="submit"
                                    whileHover={{ filter: 'brightness(1.08)' }}
                                    whileTap={{ scale: 0.97 }}
                                    disabled={loginLoading}
                                    style={{ ...styles.loginBtn, opacity: loginLoading ? 0.7 : 1, cursor: loginLoading ? 'not-allowed' : 'pointer' }}
                                >
                                    {loginLoading ? 'Autenticando...' : 'Iniciar Sesión'}
                                </motion.button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TokenLogin;
